// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageActions } from '../contexts/PageActionsContext';
import { getUrlParameter, getUserEmail } from '../config/common';
import * as featureRequestRequiredListAction from '../Action/Feature_Request_Required_List/Feature_Request_Required_List_Action';
import * as featureRequestGetListAction from '../Action/Feature_Request_Get_List/Feature_Request_Get_List_Action';
import * as contactAdminAction from '../Action/Contact_Admin/Contact_Admin_Action';
import * as feebbackAction from '../Action/Feedback/Feedback_Action';
import * as voteAction from '../Action/Vote/Vote_Action';
import { useAppDispatch } from '../hooks';
import { toast } from 'react-toastify';
import Loader from './Loader';
import PendingAccountNotice from './PendingAccountNotice';
import { isEmptyObject } from '../utils/helpers';
import { readReqStatus } from '../utils/reqStatus';

const FeatureRequests = ({ data, isDataLoaded, setIsDataLoaded, isValidUser, setIsValidUser }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [featureReqData, setFeatureReqData] = useState({
        existingVotes: [],
        featurePointsCategoryList: [],
        featurePointsStatusList: [ 'Ideas', 'Chosen', 'Delivered', 'Rejected' ]
    });
    const [filterParam, setFilterParam] = useState({
        category: '',
        seletedStatus: 'Ideas',
        sortType: 'Recent',
        searchText: '',
        showMyReq: false,
        incApproved: false
    });
    const [message, setMessage] = useState('');
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [pagination, setPagination] = useState({
        pageNo: 1,
        totalPage: 1,
        pageSize: 10
    });
    const [featureRequestList, setFeatureRequestList] = useState([]);
    const [featureReq, setFeatureReq] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isContactModal, setIsContactModal] = useState(false);
    const [isFeedbackModal, setIsFeedbackModal] = useState(false);
    const [isShareModal, setIsShareModal] = useState(false);
    // Tracks whether the list is currently locked to a shared deep-link Id.
    // Seeded once from the landing URL; never re-derived from the query string.
    const [activeSharedFeatureId, setActiveSharedFeatureId] = useState(() => getUrlParameter('Id') || '');
    const hasFetched = useRef();

    const { setPageActions } = usePageActions();
    const openContactModal = useCallback(() => setIsContactModal(true), []);
    useEffect(() => {
        setPageActions(
            <span className="small text-body-secondary me-1">
                You have {featureReqData.remainingFeaturePoints > 0 ? featureReqData.remainingFeaturePoints : 0} vote{featureReqData.remainingFeaturePoints === 1 ? '' : 's'}
            </span>
        );
        return () => setPageActions(null);
    }, [setPageActions, openContactModal, featureReqData.remainingFeaturePoints]);

    useEffect(() => {
        if (hasFetched.current) {
            return;
        }
        hasFetched.current = true;

        // ACOP-118: mirrors ReleaseNotes.tsx — a known-pending account never succeeds against any
        // /apis/featurerequest/* endpoint (all minTier "standard"), so skip them entirely instead
        // of depending on the `data` prop below, which is populated async from a separate
        // Redux round trip and isn't guaranteed to reflect pending status by the time this runs.
        if (readReqStatus() === 'Requested') {
            setIsLoading(false);
            setIsDataLoaded(true);
            setIsValidUser(false);
        }
    }, []);

    useEffect(() => {
        if(!isEmptyObject(data)){
            if (data.status === 1 && data.data && data.data.status === 'Requested') {
                setIsLoading(false);
                setIsDataLoaded(true);
                setIsValidUser(false);
                return;
            }
            setIsLoading(true);
            setIsValidUser(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if(isValidUser === false) return;

        getRequiredData(getFeatureRequests);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValidUser])

    const exitSharedFeatureFilter = useCallback(() => {
        setActiveSharedFeatureId((prev) => {
            if (!prev) return prev;
            return '';
        });
        const params = new URLSearchParams(location.search);
        if (params.has('Id')) {
            params.delete('Id');
            const nextSearch = params.toString();
            navigate(
                { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '', hash: location.hash },
                { replace: true }
            );
        }
    }, [navigate, location.pathname, location.search, location.hash]);

    const getRequiredData = (callback) => {
        var param = {};
        param.email = getUserEmail();
        dispatch(featureRequestRequiredListAction.feature_request_required_list({ data: JSON.stringify(param) })).then((response) => {
            // ACOP-118: a sub-standard-tier caller who reached this page despite the mount-time
            // readReqStatus() check (e.g. tier changed mid-session) still shouldn't see a raw
            // permission toast — fall back to the same pending notice instead.
            if (response.status === 403) {
                setIsLoading(false);
                setIsDataLoaded(true);
                setIsValidUser(false);
                return;
            }
            let existingVotes = [];
            if (response.status === 1) {
                existingVotes = response.data.existingVotes || [];
                setFeatureReqData({
                    ...featureReqData,
                    existingVotes,
                    featurePointsCategoryList: response.data.featurePointsCategoryList || [],
                    remainingFeaturePoints: response.data.remainingFeaturePoints || 0
                });
            }
            if (callback) {
                const frIdParam = activeSharedFeatureId;
                setFilterParam({...filterParam, incApproved: !!(frIdParam && frIdParam.length > 0)});
                callback(existingVotes, pagination.pageNo, frIdParam);
            } else {
                setIsLoading(false);
            }
        }).catch(error => {
            console.log(error);
            toast.error(error.message);
        })
    }

    const getFeatureRequests = (existingVotes, pageNo, frId) => {
        var param = { ...filterParam };
        param.searchText = encodeURIComponent(param.searchText);
        param.sortDirection = 'DESC';
        param.email = getUserEmail();
        param.pageSize = pagination.pageSize;
        param.pageNo = pageNo;
        param.id = frId || '';
        dispatch(featureRequestGetListAction.feature_request_get_list({ data: JSON.stringify(param) })).then((response) => {
            setIsLoading(false);
            if (response.status !== 1) {
                toast.error(response.message);
                return;
            }
            response.data.featureList.forEach((item) => {
                item.isVoted = existingVotes.includes(item.Id);
            });
            setFeatureRequestList(response.data.featureList);
            if (response.data.totalPage) {
                setPagination((prev) => ({
                    ...prev,
                    totalPage: response.data.totalPage
                }))
            }
        }).catch(error => {
            console.log(error);
            toast.error(error.message);
        })
    }

    const onApplyFilter = () => {
        exitSharedFeatureFilter();
        setIsLoading(true);
        setPagination((prev) => ({
            ...prev,
            pageNo: 1
        }));
        getFeatureRequests(featureReqData.existingVotes, 1, '');
    };

    const clearFilter = () => {
        const defaultParams = {
            category: '',
            seletedStatus: 'Ideas',
            sortType: 'Recent',
            searchText: '',
            showMyReq: false,
            incApproved: false
        };
        setFilterParam(defaultParams);
        exitSharedFeatureFilter();
        // Setting loading state and trigger data fetch with default parameters directly
        setIsLoading(true);
        setPagination(prev => ({
            ...prev,
            pageNo: 1
        }));
        // Use the default parameters directly instead of relying on state update
        const param = { ...defaultParams };
        param.searchText = encodeURIComponent(param.searchText);
        param.sortDirection = 'DESC';
        param.email = getUserEmail();
        param.pageSize = pagination.pageSize;
        param.pageNo = 1;
        param.id = '';
        
        dispatch(featureRequestGetListAction.feature_request_get_list({ data: JSON.stringify(param) })).then((response) => {
            setIsLoading(false);
            if (response.status !== 1) {
                toast.error(response.message);
                return;
            }
            const updatedList = response.data.featureList.map((item) => ({
                ...item,
                isVoted: featureReqData.existingVotes.includes(item.Id)
            }));
            setFeatureRequestList(updatedList);
            if (response.data.totalPage) {
                setPagination((prev) => ({
                    ...prev,
                    totalPage: response.data.totalPage
                }));
            }
        }).catch(error => {
            setIsLoading(false);
            console.log(error);
            toast.error(error.message);
        });
    };

    const changePage = (pageNo) => {
        exitSharedFeatureFilter();
        setPagination((prev) => ({
            ...prev,
            pageNo: pageNo
        }));
        getFeatureRequests(featureReqData.existingVotes, pageNo, '');
        setIsLoading(true);
    };

    const voteFeatureReq = (id, isRemoveVote) => {
        var param = {
            featureRequestId: id,
            email: getUserEmail()
        };
        setIsLoading(true);
        dispatch(voteAction.vote({ data: JSON.stringify(param) })).then((response) => {
            setIsLoading(false);
            if (response.status !== 1) {
                toast.error(response.message);
                return;
            }
            
            let remainingFeaturePoints = featureReqData.remainingFeaturePoints;
            remainingFeaturePoints = isRemoveVote ? remainingFeaturePoints += 1 : remainingFeaturePoints -= 1;
            toast.success(isRemoveVote ? 'You removed your vote and have ' + remainingFeaturePoints + ' vote(s) left.' : 'You voted and have ' + remainingFeaturePoints + ' vote(s) left.');
            if (isRemoveVote) {
                setFeatureRequestList((prev) => {
                    return prev.map(item => {
                        if(item.Id === id){
                            return {
                                ...item,
                                Total_Points__c: (item.Total_Points__c || 0) - 1,
                                isVoted: false
                            }
                        } else {
                            return item
                        }
                    })
                });
                setFeatureReqData({
                    ...featureReqData,
                    existingVotes: featureReqData.existingVotes.filter((vid) => vid !== id),
                    remainingFeaturePoints: remainingFeaturePoints
                });
            } else {
                setFeatureRequestList((prev) => {
                    return prev.map(item => {
                        if(item.Id === id){
                            return {
                                ...item,
                                Total_Points__c: (item.Total_Points__c || 0) + 1,
                                isVoted: true
                            }
                        } else {
                            return item
                        }
                    })
                });
                setFeatureReqData({
                    ...featureReqData,
                    existingVotes: [...featureReqData.existingVotes, id],
                    remainingFeaturePoints: remainingFeaturePoints
                });
            }
        }).catch(error => {
            console.log(error);
            toast.error(error.message);
        })
    };

    const showFeedbackModal = (index) => {
        setFeatureReq(JSON.parse(JSON.stringify(featureRequestList))[index]);
        setIsFeedbackModal(true);
    };

    const showShareModal = (index) => {
        setFeatureReq(JSON.parse(JSON.stringify(featureRequestList))[index]);
        setFeatureReq((prev) => ({
            ...prev,
            url: window.location.origin + '/feature-requests?Id=' + prev.Id
        }));
        setIsShareModal(true);
    };

    const handleInputChange = (e) => {
        exitSharedFeatureFilter();
        setFilterParam({...filterParam, [e.target.name]: e.target.value, incApproved: true});
    }

    const hideContactModal = () => {
        setIsContactModal(false);
        setMessage('');
    }

    const contactAdmin = () => {
        if (!message || message.trim().length === 0) {
            toast.error("Please enter message to continue.");
            return;
        }
        setIsLoading(true);
        let param = {
            email: getUserEmail(),
            message: message
        };
        dispatch(contactAdminAction.contact_admin({ data: JSON.stringify(param) })).then((response) => {
            setIsLoading(false);
            if (response.status !== 1) {
                toast.error(response.message);
                return;
            }
            toast.success("Contact request submitted successfully.");
            hideContactModal();
        }).catch(error => {
            console.log(error);
            toast.error(error.message);
        })
    }

    const hideFeedbackModal = () => {
        setIsFeedbackModal(false);
        setFeatureReq({});
        setFeedbackMessage('');
    }

    const hideShareModal = () => {
        setIsShareModal(false);
        setFeatureReq({});
    }

    const submitFeedback = () => {
        let message = feedbackMessage;

        if (!message || message.trim().length === 0) {
            toast.error("Please enter feedback message to continue.");
            return;
        }
    
        setIsLoading(true);
        let param = {
          email: getUserEmail(),
          message: message,
          frId: featureReq.Id,
        };
        dispatch(feebbackAction.feature_request_feedback({ data: JSON.stringify(param) })).then((response) => {
            setIsLoading(false);
            if (response.status !== 1) {
                toast.error(response.message);
                return;
            }
            toast.success("Feedback submitted successfully.");
            setFeedbackMessage('');
            hideFeedbackModal();
        }).catch(error => {
            console.log(error);
            toast.error(error.message);
        })
    }

    const copyUrl = (e, url) => {
      if (!navigator.clipboard || !url) {
        return;
      }
      navigator.clipboard.writeText(url).then(function() {
        e.currentTarget.classList.add('text-success');
        console.log('Async: Copying to clipboard was successful!');
      }, function(err) {
        console.error('Async: Could not copy url: ', err);
      });
    }

    return(
        <>
            { isLoading && <Loader /> }
            
            <div className={isValidUser ? 'container-fluid py-3 px-3 px-md-4 copilot-feature-voting-page' : 'container-fluid py-3 px-3 px-md-4'}>
                {isDataLoaded && !isValidUser && (
                    <PendingAccountNotice message="Once your account has been verified by an AvSight employee, you will be able to view and vote on Feature Requests." />
                )}
                {isValidUser && (
                    <div id="featureRequestsContainer" className="copilot-filter-layout">
                        {/* Filters */}
                        <aside className="copilot-filter-rail">
                            <h2 className="copilot-filter-rail__title">Filter by</h2>

                            <label className="form-label">Category</label>
                            <select className="form-select mb-3" name="category" value={filterParam.category} onChange={(e) => handleInputChange(e)}>
                                <option value="">All</option>
                                {featureReqData.featurePointsCategoryList &&
                                    featureReqData.featurePointsCategoryList.map((item, index) => (
                                        <option value={item.key} key={index}>{item.label}</option>
                                    ))}
                            </select>

                            <label className="form-label">Status</label>
                            <select className="form-select mb-3" name="seletedStatus" value={filterParam.seletedStatus} onChange={(e) => handleInputChange(e)}>
                                <option value="">Any</option>
                                {featureReqData.featurePointsStatusList &&
                                    featureReqData.featurePointsStatusList.map((item, index) => (
                                        <option value={item} key={index}>{item}</option>
                                    ))}
                            </select>

                            <label className="form-label">Sort By</label>
                            <select className="form-select mb-3" value={filterParam.sortType} name="sortType" onChange={(e) => handleInputChange(e)}>
                                <option value="Popular">Max points</option>
                                <option value="Recent">Most recent</option>
                            </select>

                            <label className="form-label">Keyword</label>
                            <input
                                type="text"
                                className="form-control mb-3"
                                placeholder="Search…"
                                value={filterParam.searchText}
                                name="searchText"
                                onChange={(e) => handleInputChange(e)}
                            />

                            <div className="form-check mb-3">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={filterParam.showMyReq}
                                    id="showMyReq"
                                    name="showMyReq"
                                    onChange={(e) => {
                                        exitSharedFeatureFilter();
                                        setFilterParam({ ...filterParam, showMyReq: e.target.checked, incApproved: true });
                                    }}
                                />
                                <label className="form-check-label" htmlFor="showMyReq">
                                    My Features
                                </label>
                            </div>

                            {activeSharedFeatureId ? (
                                <div className="alert alert-info small py-2 mb-3" role="status">
                                    You opened a shared link, so only this feature request is shown. Click{' '}
                                    <strong>Clear</strong> or apply a new search to see more feature requests.
                                </div>
                            ) : null}

                            {filterParam.incApproved || activeSharedFeatureId ? (
                                <button className="btn btn-primary w-100 mb-3" type="button" onClick={() => clearFilter()}>Clear</button>
                            ) : null}
                            <button className="btn btn-primary w-100" type="button" onClick={() => onApplyFilter()}>
                                Apply
                            </button>
                        </aside>

                        {/* Feature Request List */}
                        <main className="flex-grow-1 min-w-0 ms-lg-4 mt-3 mt-lg-0">
                            {pagination.totalPage > 1 && (
                                <div className="row px-2 mb-2">
                                    <div className="col-12 background py-2 text-end">
                                        <button className="btn btn-primary me-2 shrink" onClick={() => changePage(pagination.pageNo - 1)} disabled={pagination.pageNo === 1}>Previous</button>
                                        {pagination.pageNo}&nbsp;/&nbsp;{pagination.totalPage}
                                        <button className="btn btn-primary ms-2 shrink" onClick={() => changePage(pagination.pageNo + 1)} disabled={pagination.pageNo >= pagination.totalPage}>Next</button>
                                    </div>
                                </div>
                            )}

                            {featureRequestList && featureRequestList.length === 0 ? (
                                <div className="text-center">
                                    <h6>No Data Available</h6>
                                </div>
                            ) : (
                                featureRequestList &&
                                featureRequestList.map((fItem, i) => (
                                    <div
                                        key={i}
                                        data-index={i}
                                        className="copilot-changelog-card copilot-support-list-card mb-3"
                                        data-type="view"
                                    >
                                        <div className="copilot-changelog-card__accent copilot-changelog-card__accent--muted" aria-hidden />
                                        <div className="copilot-changelog-card__body">
                                            <div className="row">
                                                <div className="col-md-10">
                                                    <h3 className="copilot-changelog-section-title mb-1">{fItem.Name}</h3>
                                                    <p className="text-muted small mb-2">{fItem.Feature_Description__c}</p>
                                                    <div className="d-flex align-items-center flex-wrap gap-2">
                                                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => showFeedbackModal(i)}>
                                                            Feedback
                                                        </button>
                                                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => showShareModal(i)}>
                                                            Share
                                                        </button>
                                                        <div className="idea-subtitle text-muted small ms-1">
                                                            <span>
                                                                {(fItem.Category__c || 'NA').replace(/;\s*/g, '; ')}
                                                            </span>
                                                            {fItem.Status__c === 'New' || fItem.Status__c === 'Verified' || fItem.Status__c === 'On Roadmap' ? (
                                                                <span className="idea-status-label">Ideas</span>
                                                            ) : fItem.Status__c === 'In Development' ? (
                                                                <span className="idea-status-label">Chosen</span>
                                                            ) : fItem.Status__c === 'Delivered' || fItem.Status__c === 'Merged' || fItem.Status__c === 'Existing Feature' ? (
                                                                <span className="idea-status-label">Delivered</span>
                                                            ) : fItem.Status__c === 'Not for AvS Core' ? (
                                                                <span className="idea-status-label">Rejected</span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="vote-container text-end">
                                                        <div className="vote-points text-muted small mb-2">
                                                            {fItem.Total_Points__c} POINT{fItem.Total_Points__c > 1 ? 'S' : ''}
                                                        </div>
                                                        {!fItem.isVoted &&
                                                        featureReqData.remainingFeaturePoints > 0 &&
                                                        (fItem.Status__c === 'New' ||
                                                            fItem.Status__c === 'Verified' ||
                                                            fItem.Status__c === 'On Roadmap') ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm"
                                                                data-type="vote"
                                                                onClick={() => voteFeatureReq(fItem.Id, false)}
                                                            >
                                                                Upvote
                                                            </button>
                                                        ) : fItem.isVoted &&
                                                          (fItem.Status__c === 'New' ||
                                                              fItem.Status__c === 'Verified' ||
                                                              fItem.Status__c === 'On Roadmap') ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm"
                                                                data-type="vote"
                                                                onClick={() => voteFeatureReq(fItem.Id, true)}
                                                            >
                                                                Remove Vote
                                                            </button>
                                                        ) : fItem.Status__c !== 'New' &&
                                                          fItem.Status__c !== 'Verified' &&
                                                          fItem.Status__c !== 'On Roadmap' ? (
                                                            <span className="badge text-bg-secondary">
                                                                {fItem.Status__c === 'In Development'
                                                                    ? 'Chosen'
                                                                    : fItem.Status__c === 'Delivered' ||
                                                                        fItem.Status__c === 'Merged' ||
                                                                        fItem.Status__c === 'Existing Feature'
                                                                      ? 'Delivered'
                                                                      : fItem.Status__c === 'Not for AvS Core'
                                                                        ? 'Rejected'
                                                                        : null}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}

                            {pagination.totalPage > 1 && (
                                <div className="row px-2 mt-2">
                                    <div className="col-12 background py-2 text-end">
                                        <button className="btn btn-primary me-2 shrink" onClick={() => changePage(pagination.pageNo - 1)} disabled={pagination.pageNo === 1}>Previous</button>
                                        {pagination.pageNo}&nbsp;/&nbsp;{pagination.totalPage}
                                        <button className="btn btn-primary ms-2 shrink" onClick={() => changePage(pagination.pageNo + 1)} disabled={pagination.pageNo >= pagination.totalPage}>Next</button>
                                    </div>
                                </div>
                            )}
                        </main>
                    </div>
                )}
            </div>

            {/* contact professional services */}
            {isContactModal && (
                <>
                    <div
                        className="modal fade show copilot-changelog-upgrade-modal-root"
                        id="frContact"
                        role="dialog"
                        aria-labelledby="frContactLabel"
                        aria-modal="true"
                        aria-hidden="false"
                        style={{ display: 'block' }}
                    >
                        <div className="modal-dialog modal-dialog-centered mw-570 px-2" role="document">
                            <div className="modal-content copilot-changelog-upgrade-modal">
                                <div className="copilot-changelog-upgrade-modal__accent" aria-hidden />
                                <div className="modal-header copilot-changelog-upgrade-modal__header border-0">
                                    <div className="flex-grow-1 min-w-0 pe-2">
                                        <h2
                                            className="copilot-changelog-section-title copilot-changelog-upgrade-modal__title"
                                            id="frContactLabel"
                                        >
                                            Contact AvSight Professional Services
                                        </h2>
                                        <p className="copilot-changelog-section-lead copilot-changelog-upgrade-modal__lead mb-0 mt-1">
                                            Request a quote for custom development—we&apos;ll follow up
                                            with next steps.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close copilot-changelog-upgrade-modal__close flex-shrink-0 mt-0"
                                        aria-label="Close"
                                        onClick={hideContactModal}
                                    />
                                </div>
                                <div className="modal-body copilot-changelog-upgrade-modal__body">
                                    <form
                                        id="feature-contact-ps-form"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            contactAdmin();
                                        }}
                                    >
                                        <div className="copilot-changelog-upgrade-modal__fields">
                                            <label
                                                className="copilot-changelog-upgrade-modal__label"
                                                htmlFor="feature-contact-ps-message"
                                            >
                                                Message
                                            </label>
                                            <textarea
                                                id="feature-contact-ps-message"
                                                className="form-control form-control-sm login-form-control copilot-changelog-upgrade-modal__control copilot-changelog-upgrade-modal__textarea rounded-3"
                                                rows={4}
                                                placeholder="Describe what you need…"
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                            />
                                        </div>
                                    </form>
                                </div>
                                <div className="modal-footer copilot-changelog-upgrade-modal__footer border-0 gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary login-form-secondary"
                                        onClick={hideContactModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        form="feature-contact-ps-form"
                                        className="btn btn-sm btn-primary login-form-submit"
                                    >
                                        Send
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show copilot-changelog-upgrade-modal-backdrop" />
                </>
            )}

            {/* send feature request feedback */}
            {isFeedbackModal && (
                <>
                    <div
                        className="modal fade show copilot-changelog-upgrade-modal-root"
                        id="frFeedback"
                        role="dialog"
                        aria-labelledby="frFeedbackLabel"
                        aria-modal="true"
                        aria-hidden="false"
                        style={{ display: 'block' }}
                    >
                        <div className="modal-dialog modal-dialog-centered mw-570 px-2" role="document">
                            <div className="modal-content copilot-changelog-upgrade-modal">
                                <div className="copilot-changelog-upgrade-modal__accent" aria-hidden />
                                <div className="modal-header copilot-changelog-upgrade-modal__header border-0">
                                    <div className="flex-grow-1 min-w-0 pe-2">
                                        <h2
                                            className="copilot-changelog-section-title copilot-changelog-upgrade-modal__title"
                                            id="frFeedbackLabel"
                                        >
                                            Send feedback
                                        </h2>
                                        <p className="copilot-changelog-section-lead copilot-changelog-upgrade-modal__lead mb-0 mt-1">
                                            {featureReq.Name
                                                ? `About: ${featureReq.Name}`
                                                : 'Your message is sent to the product team.'}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close copilot-changelog-upgrade-modal__close flex-shrink-0 mt-0"
                                        aria-label="Close"
                                        onClick={hideFeedbackModal}
                                    />
                                </div>
                                <div className="modal-body copilot-changelog-upgrade-modal__body">
                                    <form
                                        id="feature-feedback-form"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            submitFeedback();
                                        }}
                                    >
                                        <div className="copilot-changelog-upgrade-modal__fields">
                                            <label
                                                className="copilot-changelog-upgrade-modal__label"
                                                htmlFor="feature-feedback-message"
                                            >
                                                Message
                                            </label>
                                            <textarea
                                                id="feature-feedback-message"
                                                className="form-control form-control-sm login-form-control copilot-changelog-upgrade-modal__control copilot-changelog-upgrade-modal__textarea rounded-3"
                                                rows={4}
                                                placeholder="Type your feedback…"
                                                value={feedbackMessage}
                                                onChange={(e) => setFeedbackMessage(e.target.value)}
                                            />
                                        </div>
                                    </form>
                                </div>
                                <div className="modal-footer copilot-changelog-upgrade-modal__footer border-0 gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary login-form-secondary"
                                        onClick={hideFeedbackModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        form="feature-feedback-form"
                                        className="btn btn-sm btn-primary login-form-submit"
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show copilot-changelog-upgrade-modal-backdrop" />
                </>
            )}

            {/* share feature request url */}
            {isShareModal && (
                <>
                    <div
                        className="modal fade show copilot-changelog-upgrade-modal-root"
                        id="frShare"
                        role="dialog"
                        aria-labelledby="frShareLabel"
                        aria-modal="true"
                        aria-hidden="false"
                        style={{ display: 'block' }}
                    >
                        <div className="modal-dialog modal-dialog-centered mw-570 px-2" role="document">
                            <div className="modal-content copilot-changelog-upgrade-modal">
                                <div className="copilot-changelog-upgrade-modal__accent" aria-hidden />
                                <div className="modal-header copilot-changelog-upgrade-modal__header border-0">
                                    <div className="flex-grow-1 min-w-0 pe-2">
                                        <h2
                                            className="copilot-changelog-section-title copilot-changelog-upgrade-modal__title"
                                            id="frShareLabel"
                                        >
                                            Share feature request
                                        </h2>
                                        <p className="copilot-changelog-section-lead copilot-changelog-upgrade-modal__lead mb-0 mt-1">
                                            Copy this link so others can open the same idea in the
                                            repository.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close copilot-changelog-upgrade-modal__close flex-shrink-0 mt-0"
                                        aria-label="Close"
                                        onClick={hideShareModal}
                                    />
                                </div>
                                <div className="modal-body copilot-changelog-upgrade-modal__body">
                                    <div className="copilot-changelog-upgrade-modal__fields">
                                        <label
                                            className="copilot-changelog-upgrade-modal__label"
                                            htmlFor="feature-share-url"
                                        >
                                            Link
                                        </label>
                                        <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-center copilot-changelog-upgrade-modal__url-row">
                                            <input
                                                id="feature-share-url"
                                                type="text"
                                                readOnly
                                                className="form-control form-control-sm login-form-control copilot-changelog-upgrade-modal__control rounded-3 flex-grow-1"
                                                value={featureReq.url || ''}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary login-form-secondary flex-shrink-0"
                                                disabled={!featureReq?.Id}
                                                onClick={(e) => copyUrl(e, featureReq.url)}
                                                aria-label="Copy link to clipboard"
                                            >
                                                <i className="fa-solid fa-copy me-1" aria-hidden />
                                                Copy
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer copilot-changelog-upgrade-modal__footer border-0 gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary login-form-secondary"
                                        onClick={hideShareModal}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show copilot-changelog-upgrade-modal-backdrop" />
                </>
            )}
        </>
    );
};

export default FeatureRequests;
